import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPlay, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { formatAiText } from "../../utils/formatAiText";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const authHeadersJson = () => ({ "Content-Type": "application/json", ...authHeaders() });

const LessonDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("lesson"); // lesson | flashcards | practice

    const [practiceQuestions, setPracticeQuestions] = useState([]);
    const [testId, setTestId] = useState(null);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [finished, setFinished] = useState(false);

    useEffect(() => {
        const fetchLesson = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/lessons/${id}`, { headers: authHeaders() });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Lesson not found");
                setLesson(data.data.lesson);
                setFlashcards(data.data.flashcards || []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchLesson();
    }, [id]);

    const startPractice = async () => {
        try {
            const [questionsRes, testRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/lessons/${id}/practice`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/api/tests`, { headers: authHeaders() }),
            ]);
            const questionsData = await questionsRes.json();
            const testData = await testRes.json();
            if (!questionsData.data?.length) {
                toast.error("No practice questions are ready for this lesson yet.");
                return;
            }
            setPracticeQuestions(questionsData.data);
            setTestId(testData.data.id);
            setCurrent(0);
            setAnswers({});
            setFinished(false);
            setView("practice");
        } catch (err) {
            toast.error("Could not start practice");
        }
    };

    const selectOption = async (question, letter) => {
        if (answers[question.id]) return; // already answered
        setAnswers((prev) => ({ ...prev, [question.id]: letter }));
        try {
            await fetch(`${API_BASE_URL}/api/answer`, {
                method: "POST",
                headers: authHeadersJson(),
                body: JSON.stringify({
                    question_id: question.id,
                    selected_option: letter,
                    test_id: testId,
                    mode: "lesson_practice",
                    topic: question.topic,
                    subject: question.subject,
                    answerType: "personal",
                }),
            });
        } catch (err) {
            // Answer is still shown locally even if the network call fails.
        }
    };

    const finishPractice = async () => {
        try {
            await fetch(`${API_BASE_URL}/api/tests/${testId}/submit`, { method: "PATCH", headers: authHeaders() });
            await fetch(`${API_BASE_URL}/api/lessons/${id}/complete`, {
                method: "POST",
                headers: authHeadersJson(),
                body: JSON.stringify({ test_id: testId }),
            });
            setFinished(true);
        } catch (err) {
            toast.error("Could not save your practice results");
        }
    };

    if (loading) return <div className="p-10 text-gray-500">Loading...</div>;
    if (!lesson) return <div className="p-10 text-gray-500">Lesson not found.</div>;

    const correctCount = practiceQuestions.filter((q) => answers[q.id] === q.correctAnswer).length;

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50">
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                    <button onClick={() => (view === "lesson" ? navigate("/lessons") : setView("lesson"))} className="text-white">
                        <FaArrowLeft size={18} />
                    </button>
                    <h1 className="text-xl font-bold text-white">{lesson.title}</h1>
                </nav>

                <div className="max-w-3xl mx-auto p-6">
                    {view === "lesson" && (
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{lesson.body}</p>
                            </div>
                            {lesson.ai_summary && (
                                <div className="bg-blue-50 rounded-xl p-6">
                                    <h3 className="font-semibold text-blue-800 mb-2">Key Concepts</h3>
                                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{formatAiText(lesson.ai_summary)}</p>
                                </div>
                            )}
                            <div className="flex gap-3">
                                {flashcards.length > 0 && (
                                    <button onClick={() => setView("flashcards")} className="bg-white border rounded-lg px-4 py-2 font-semibold text-gray-700">
                                        Flashcards ({flashcards.length})
                                    </button>
                                )}
                                <button onClick={startPractice} className="bg-blue600 text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2">
                                    <FaPlay /> Take Practice
                                </button>
                            </div>
                        </div>
                    )}

                    {view === "flashcards" && (
                        <div className="grid grid-cols-2 gap-3">
                            {flashcards.map((f) => (
                                <div key={f.id} className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="font-medium text-gray-800 text-sm">{f.front}</p>
                                    <p className="text-sm text-gray-500 mt-2 border-t pt-2">{f.back}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {view === "practice" && !finished && practiceQuestions[current] && (
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <p className="text-xs text-gray-400 mb-2">Question {current + 1} of {practiceQuestions.length}</p>
                            <MathText text={practiceQuestions[current].question} className="font-medium text-gray-800 block mb-4" />
                            <div className="space-y-2">
                                {["A", "B", "C", "D"].map((letter) => {
                                    const q = practiceQuestions[current];
                                    const chosen = answers[q.id];
                                    const isChosen = chosen === letter;
                                    const isCorrectLetter = q.correctAnswer === letter;
                                    let style = "border-gray-200 hover:border-blue600";
                                    if (chosen) {
                                        if (isCorrectLetter) style = "border-green-500 bg-green-50";
                                        else if (isChosen) style = "border-red-500 bg-red-50";
                                    }
                                    return (
                                        <button key={letter} onClick={() => selectOption(q, letter)}
                                            className={`w-full text-left border rounded-lg p-3 flex items-center justify-between ${style}`}>
                                            <span><span className="font-semibold mr-2">{letter}.</span><MathText text={q[`option_${letter.toLowerCase()}`]} /></span>
                                            {chosen && isCorrectLetter && <FaCheckCircle className="text-green-500" />}
                                            {chosen && isChosen && !isCorrectLetter && <FaTimesCircle className="text-red-500" />}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-6">
                                <button disabled={current === 0} onClick={() => setCurrent((c) => c - 1)} className="text-gray-500 disabled:opacity-30">Previous</button>
                                {current < practiceQuestions.length - 1 ? (
                                    <button onClick={() => setCurrent((c) => c + 1)} className="bg-blue600 text-white px-4 py-2 rounded-lg font-semibold">Next</button>
                                ) : (
                                    <button onClick={finishPractice} className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold">Finish</button>
                                )}
                            </div>
                        </div>
                    )}

                    {view === "practice" && finished && (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                {correctCount}/{practiceQuestions.length} correct
                            </h3>
                            <p className="text-gray-500 mb-6">Great work — this feeds into your Study Plan and Readiness score.</p>
                            <button onClick={() => setView("lesson")} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold">Back to Lesson</button>
                        </div>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

export default LessonDetail;
